// angular
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
// components
import { NoContentComponent } from '@app/core/components/no-content/no-content.component';
import { MediaPhotosGridComponent } from '@app/core/components/media-photos-grid/media-photos-grid.component';
import { MediaVideosGridComponent } from '@app/core/components/media-videos-grid/media-videos-grid.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
import { ProjectQuoteDialogComponent } from '@app/core/dialogs/project-quote-dialog/project-quote-dialog.component';
// services
import { ProjectsService } from '../shared/services/projects.service';
import { UserService } from '@app/shared/services/user.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// utils
import {
  projectDisplayFirstName,
  projectDisplayLastName,
  projectHasRealOwnerName,
  shouldBlurProjectOwnerField,
} from '../shared/utils/project-display.util';
import {
  canShowProjectQuoteButton,
  resolveProjectQuoteMemberType,
} from '../shared/utils/project-quote-access.util';
import { canViewSolutionsProfileDetails } from '@app/+solutions/shared/utils/solutions-profile-access.util';
import { getAvailableTranslation } from '@app/shared/utils/get-available-translation';
import { I18n } from '@app/shared/enums/i18n.enum';
import type { ProjectQuoteMemberType } from '../shared/types/project-quote.interface';

@Component({
  selector: 'pwj-projects-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    TranslateDataPipe,
    NgxSkeletonLoaderModule,
    NoContentComponent,
    MediaPhotosGridComponent,
    MediaVideosGridComponent,
    ButtonComponent,
    DatePipe,
  ],
  templateUrl: './projects-detail.component.html',
  styleUrls: [
    './projects-detail.component.scss',
    '../../+solutions/person-detail/person-detail.component.scss',
  ],
})
export class ProjectsDetailComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _projectsService = inject(ProjectsService);
  private readonly _userService = inject(UserService);
  private readonly _dialogService = inject(DialogService);
  private readonly _titleService = inject(Title);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);

  private readonly _quoteMemberType = signal<ProjectQuoteMemberType | null>(null);

  readonly project = computed(() => this._projectsService.projectDetail());
  readonly isLoading = computed(() => this._projectsService.isProjectDetailLoading());
  readonly hasRealOwner = computed(() =>
    projectHasRealOwnerName(this.project()?.owner)
  );
  readonly canViewOwnerDetails = computed(() =>
    canViewSolutionsProfileDetails(this._userService.userSignal())
  );
  readonly isOwnerLocked = computed(() =>
    shouldBlurProjectOwnerField(this.hasRealOwner())
  );
  readonly displayFirstName = computed(() => {
    const data = this.project();
    if (!data) return '';
    return projectDisplayFirstName(data, this.hasRealOwner());
  });
  readonly displayLastName = computed(() => {
    const data = this.project();
    if (!data) return '';
    return projectDisplayLastName(data, this.hasRealOwner());
  });
  readonly projectPhotos = computed(() => this.project()?.photos ?? []);
  readonly projectVideos = computed(() => this.project()?.videos ?? []);
  readonly hasContent = computed(() => {
    const data = this.project();
    return !!(data?._id);
  });
  readonly quoteMemberType = computed(() => {
    const resolved = this._quoteMemberType();
    if (resolved) return resolved;
    return resolveProjectQuoteMemberType(this._userService.userSignal());
  });
  readonly canSubmitQuote = computed(() =>
    canShowProjectQuoteButton(
      this._userService.userSignal(),
      this.project(),
      this.quoteMemberType()
    )
  );
  readonly isQuoteAlreadySent = computed(
    () => this.project()?.already_sent_request === true
  );

  constructor() {
    effect(() => {
      const data = this.project();
      if (!data?.title) return;
      const title = getAvailableTranslation(data.title, I18n.en);
      if (title && title !== '-') {
        this._titleService.setTitle(title);
      }
    });

  }

  ngOnInit(): void {
    toObservable(this._userService.userSignal, { injector: this._injector })
      .pipe(
        map(user => (user?.roles?.length ?? 0) > 0),
        distinctUntilChanged(),
        switchMap(isLoggedIn => {
          if (!isLoggedIn) return of(null);
          return this._projectsService.resolveCurrentQuoteMemberType$();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(type => this._quoteMemberType.set(type));

    this._route.paramMap
      .pipe(
        map(params => params.get('projectId')),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(id => {
        this._projectsService.getProjectDetail(id);
      });
  }

  projectStatusKey(): string {
    return `enums.projectStatus.${this.project()?.status ?? 'OPEN'}`;
  }

  ownerTypeKey(): string {
    const type = this.project()?.owner?.type ?? 'PROJECT_OWNER';
    return `projects.ownerTypes.${type}`;
  }

  openQuoteDialog(): void {
    if (this.isQuoteAlreadySent()) return;

    const projectId = this.project()?._id;
    const memberType = this.quoteMemberType();
    if (!projectId || !memberType) return;

    const dialogRef = this._dialogService.open(ProjectQuoteDialogComponent, {
      width: 'none',
      height: '36em',
      data: {
        title: 'projects.quote.submit',
        memberType,
      },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this._projectsService.submitProjectQuote(projectId, res);
      }
    });
  }
}
